@<template>
  <div>
    <base-modal
      :ref="modal.key"
      :show="open"
      :title="modal.title"
      @on-cancel="hide()"
      :closeText="closeButtonText"
      :showConfirmButton="changes"
      @on-confirm="saveChanges()"
      fullscreen
    >
      <v-row no-gutters class="pa-0 ma-0">
        <v-col>
          <v-row no-gutters class="pa-0 ma-0 justify-space-around">
            <v-radio-group v-model="currentState" row @change="onStateChanged">
                <v-radio
                    :label="$t('ExpiredPlural')"
                    :color="settings.expiredColor || 'error'"
                    value="Expired"
                    class="custom-radio"
                />
                <v-radio
                    :label="`${$t('ExpireInLessThan')} ${warningDays} ${$t('Days')}`"
                    :color="settings.warningColor || 'warning'"
                    value="Warning"
                    class="custom-radio"
                />
                <v-radio
                    :label="$t('OK')"
                    value="OnTime"
                    :color="settings.onTimeColor || 'success'"
                    class="custom-radio"
                />
            </v-radio-group>
          </v-row>
          <v-row no-gutters class="pa-0 ma-0" v-if="!loading">
            <v-col>
              <DxDataGrid
                :ref="gridKey"
                v-if="!loading"
                id="gridContainer"
                :data-source="dataSource"
                :show-borders="true"
                :allow-column-resizing="true"
                :column-auto-width="settings.enableHorizontalScrolling"
                key-expr="Id"
                @row-prepared="onRowPrepared"
                @toolbar-preparing="onToolbarPreparing($event)"
                @exporting="onExporting($event)"
                @selection-changed="onSelectionChanged"
                @row-updated="onRowUpdated"
                @editing-start="onEditingStart"
              >
                <dx-search-panel
                  :visible="true"
                  :highlight-case-sensitive="true"
                />
                <dx-filter-panel :visible="true" />
                <dx-filter-row :visible="true" />
                <dx-group-panel :visible="true" class="groupPanel" />
                <dxSelection show-check-boxes-mode="always" mode="multiple" />
                <DxPaging :page-size="20" />
                <DxPager
                  :visible="true"
                  :allowed-page-sizes="[5, 10, 20, 50]"
                  :show-page-size-selector="true"
                  :show-info="true"
                  :show-navigation-buttons="true"
                />
                <template v-for="(column, index) in dataColumns">
                  <DxColumn
                    v-if="!column.dynamicLookup"
                    :caption="column.title"
                    :data-field="column.name"
                    :allow-editing="column.allowEdit"
                    :key="index"
                    :visible="column.visible"
                    :format="(value) => formatColumn(column, value)"
                  >
                    <DxRequiredRule v-if="column.isRequired" />
                    <DxLookup
                      v-if="column.staticLookup"
                      :data-source="column.items"
                      display-expr="displayValue"
                      value-expr="displayValue"
                    />
                  </DxColumn>

                  <DxColumn
                    v-if="column.dynamicLookup"
                    :caption="column.title"
                    :data-field="column.name"
                    :allow-editing="column.allowEdit"
                    :key="index"
                    :visible="column.visible"
                    cell-template="lookupCellTemplate"
                    edit-cell-template="dropDownBoxEditor"
                  >
                    <DxRequiredRule v-if="column.isRequired" />
                  </DxColumn>
                </template>
                <template #lookupCellTemplate="{ data }">
                  {{ data.value }}
                </template>
                <template #dropDownBoxEditor="{ data }">
                  <div>
                    <DxLookup2
                      :ref="`lookup-${data.key}-${data.columnIndex - 1}`"
                      :data-source="
                        getLookupDataSource(data.key, data.columnIndex)
                      "
                      display-expr="text"
                      value-expr="text"
                      :value="data.value"
                      @value-changed="(e) => setCellValue(e, data)"
                    />
                  </div>
                </template>

                <dx-column
                  alignment="right"
                  :allow-sorting="false"
                  :allow-filtering="false"
                  cell-template="customActionTemplates"
                  :visible="true"
                  :min-width="80"
                />

                <DxSummary>
                  <DxGroupItem
                    v-for="(column, index) in numericColumns"
                    :key="`g-${index}`"
                    :column="column"
                    summary-type="sum"
                    :value-format="formatGroupedSummary"
                  />
                </DxSummary>
                <DxEditing mode="row" :allow-updating="true" />

                <template #checkData>
                  <div>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          icon
                          color="grey"
                          @click.prevent="validate"
                          v-on="on"
                        >
                          <v-icon color="green" large>mdi-check-bold</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ `${$t("Validate")}` }}</span>
                    </v-tooltip>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          icon
                          color="grey"
                          @click.prevent="refuse"
                          v-on="on"
                        >
                          <v-icon color="red" large>mdi-close-thick</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ `${$t("Refuse")}` }}</span>
                    </v-tooltip>
                    <v-menu bottom>
                      <template v-slot:activator="{ on: menu, attrs }">
                        <v-tooltip top>
                          <template v-slot:activator="{ on: tooltip }">
                            <a
                              v-bind="attrs"
                              v-on="{ ...tooltip, ...menu }"
                              @click="loadToolbarPluginList()"
                            >
                              <v-icon color="blue" large>mdi-power-plug</v-icon>
                            </a>
                          </template>
                          <span>{{ $t("Plugins") }}</span>
                        </v-tooltip>
                      </template>
                      <v-list>
                        <v-list-item-group>
                          <v-list-item
                            v-for="plugin in toolbarPluginList"
                            v-bind:key="plugin.id"
                            @click="loadPlugin(plugin, selectedDocuments)"
                          >
                            <v-list-item-icon>
                              <v-icon>{{ plugin.icon }}</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>{{
                                plugin.name
                              }}</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list-item-group>
                      </v-list>
                    </v-menu>
                  </div>
                </template>

                <template #exportButtonTemplate>
                  <div>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          icon
                          color="green"
                          @click.prevent="exportGrid"
                          v-on="on"
                        >
                          <v-icon large>mdi-microsoft-excel</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ `${$t("Export")} (.xlsx)` }}</span>
                    </v-tooltip>
                  </div>
                </template>

                <template slot="customActionTemplates" slot-scope="row">
                  <div>
                    <v-menu left offset-x offset-y>
                      <template v-slot:activator="{ on: menu, attrs }">
                        <v-tooltip top>
                          <template v-slot:activator="{ on: tooltip }">
                            <a
                              v-bind="attrs"
                              v-on="{ ...tooltip, ...menu }"
                              @click="loadPluginList(row.data.key)"
                              class="px-2"
                            >
                              <v-icon color="blue">mdi-power-plug</v-icon>
                            </a>
                          </template>
                          <span>{{ $t("Plugins") }}</span>
                        </v-tooltip>
                      </template>
                      <v-list>
                        <v-list-item-group>
                          <v-list-item
                            v-for="plugin in pluginList"
                            v-bind:key="plugin.id"
                            @click="loadPlugin(plugin, row.data.key)"
                          >
                            <v-list-item-icon>
                              <v-icon>{{ plugin.icon }}</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>{{
                                plugin.name
                              }}</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list-item-group>
                      </v-list>
                    </v-menu>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <a
                          v-on="on"
                          @click.prevent="loadDocument(row.data.key)"
                          class="px-2"
                        >
                          <v-icon color="blue">mdi-file-document</v-icon>
                        </a>
                      </template>
                      <span>{{ $t("ShowMainDocument") }}</span>
                    </v-tooltip>
                  </div>
                </template>
              </DxDataGrid>
            </v-col>
          </v-row>
        </v-col>
        <v-col v-show="showDocument">
          <v-card class="pt-1">
            <v-app-bar flat dense color="grey lighten-4">
              <v-btn icon @click="showDocument = false" class="ml-1">
                <v-icon large>mdi-close</v-icon>
              </v-btn>
            </v-app-bar>
            <v-card-text class="pa-0">
              <iframe
                :src="url"
                width="100%"
                :style="getHeight"
                scrolling="no"
                >{{ $t("Error") }}</iframe
              >
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row justify="center" align="center" v-if="loading">
        <div class="text-center">
          <div>
            <v-progress-circular
              class="my-4"
              :size="50"
              width="5"
              color="primary"
              indeterminate
            ></v-progress-circular>
          </div>
          <p class="display-1">{{ $t("Loading") }}...</p>
        </div>
      </v-row>
    </base-modal>
    <ValidationModal :ref="validationModalKey" @on-validated="onValidated" />
    <PluginLoaderModal
      :ref="pluginLoaderModalKey"
      @on-refresh-results="onValidated"
    />
  </div>
</template>
<script>
import { exportDataGrid } from 'devextreme/excel_exporter';

import CustomStore from 'devextreme/data/custom_store';
import { Workbook } from 'exceljs';
import DocumentService from '@/services/DocumentService.js';
import ContentTypeService from '@/services/ContentTypeService';
import ValidationModal from '@/components/search/results/ValidationModal.vue';
import saveAs from 'file-saver';
import Utils from '@/ecm/common/Utils';
import DocumentTrackerSettings from '@/ecm/models/widget/documentTracker/DocumentTrackerSettings';
import cloneDeep from 'lodash/cloneDeep';
import SearchService from '@/services/SearchService';
import PluginService from '@/services/PluginService';
import PluginLoaderModal from '@/components/search/results/PluginLoaderModal.vue';
import Plugin from '@/ecm/models/plugin/Plugin';

import {
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxPager,
    DxFilterPanel,
    DxSearchPanel,
    DxGroupPanel,
    DxSelection,
    DxFilterRow,
    DxSummary,
    DxGroupItem,
    DxEditing,
    DxRequiredRule,
    DxLookup,
} from 'devextreme-vue/data-grid';

import DxLookup2 from 'devextreme-vue/lookup';

import * as frMessages from 'devextreme/localization/messages/fr.json';
import * as deMessages from 'devextreme/localization/messages/de.json';
import * as itMessages from 'devextreme/localization/messages/it.json';
import * as ptMessages from 'devextreme/localization/messages/pt.json';
import * as ruMessages from 'devextreme/localization/messages/ru.json';
import * as esMessages from 'devextreme/localization/messages/es.json';

import { locale, loadMessages } from 'devextreme/localization';
import globalConfig from 'devextreme/core/config';

import FieldType from '@/ecm/variables/FieldType';
import WidgetService from '@/services/WidgetService';
import WidgetType from '@/ecm/variables/WidgetType';
import DocumentTrackerData from '@/ecm/models/widget/documentTracker/DocumentTrackerData';
import { WidgetDocumentTrackerStates } from '@/ecm/variables/WidgetDocumentTrackerStates';

export default {
    name: 'DocumentTrackerDataModal',
    components: {
        DxDataGrid,
        DxColumn,
        DxPaging,
        DxPager,
        DxFilterPanel,
        DxSearchPanel,
        DxGroupPanel,
        DxSelection,
        DxFilterRow,
        DxSummary,
        DxGroupItem,
        DxEditing,
        ValidationModal,
        DxRequiredRule,
        DxLookup,
        DxLookup2,
        PluginLoaderModal,
    },
    props: {
        widgetTitle: {
            type: String,
            required: true,
        },
        warningDays: {
            type: Number,
            required: true,
        },
    },
    created() {
        const currentCulture = this.$store.getters['account/user'].CultureCode;
        const userDecSep = this.$store.getters['account/user'].MetricDecimalSep;
        const userThouSep = this.$store.getters['account/user'].MetricThousandSep;
        locale(currentCulture);
        globalConfig({
            decimalSeparator: userDecSep,
            thousandsSeparator: userThouSep,
        });
        switch (currentCulture) {
        case 'fr':
            loadMessages(frMessages.default);
            break;
        case 'de':
            loadMessages(deMessages.default);
            break;
        case 'it':
            loadMessages(itMessages.default);
            break;
        case 'pt':
            loadMessages(ptMessages.default);
            break;
        case 'ru':
            loadMessages(ruMessages.default);
            break;
        case 'es':
            loadMessages(esMessages.default);
            break;
        }
    },
    data() {
        return {
            modal: {
                title: this.$t('WidgetDocumentTrackerTitle'),
                key: 'widgetDocumentTrackerModalKey',
            },
            gridKey: 'documentTrackerGrid',
            validationModalKey: 'documentTrackerValidationModalKey',
            pluginLoaderModalKey: 'pluginLoaderModalKey',
            showDocument: false,
            file: '',
            selectedDocuments: [],
            modifiedDocuments: [],
            dataToDisplay: [],
            dataColumns: [],
            numericColumns: [],
            open: false,
            changes: false,
            currentState: WidgetDocumentTrackerStates.Expired,
            loading: false,
            settings: new DocumentTrackerSettings(),
            displayedDocumentId: 0,
            documentList: [],
            toolbarPluginList: [],
            pluginList: [],
            listElementsForCodes: []
        };
    },
    computed: {
        modalInstance() {
            return this.$refs[this.modal.key];
        },
        validationModalInstance() {
            return this.$refs[this.validationModalKey];
        },
        dataSource() {
            const self = this;
            const dataSource = [...this.dataToDisplay]
                .filter((d) => self.currentState === d.state)
                .map((row) => {
                    const gridRow = {
                        Id: row.objectId,
                        State: row.state,
                    };
                    row.fields.forEach((f) => {
                        gridRow[f.code.replaceAll(' ', '_')] = f.value;
                    });
                    return gridRow;
                });
            dataSource.sort(function (a, b) {
                return a.Id - b.Id;
            });
            return dataSource;
        },
        url() {
            return `/js/pdf/web/viewer.html?file=${this.file}`;
        },
        getHeight() {
            return 'height: ' + (window.innerHeight - 43) + 'px';
        },
        closeButtonText() {
            return this.changes ? this.$t('Cancel') : this.$t('Close');
        },
    },
    methods: {
        mapDocumentsData(documentTrackerData) {
            const fieldsToShow = this.settings.FieldsToShow;
            const data = documentTrackerData.Documents.map(
                (d) => new DocumentTrackerData(d, this.settings.SelectedDocumentState, fieldsToShow)
            );
            return data;
        },
        formatColumn(column, value) {
            if (column.isNumber && value) {
                const userDecSep = this.$store.getters['account/user'].MetricDecimalSep;
                const userThouSep = this.$store.getters['account/user'].MetricThousandSep;
                return Utils.number.toUserFormat(
                    parseFloat(value),
                    userDecSep,
                    userThouSep
                );
            }
            return value;
        },
        formatGroupedSummary(value) {
            if (value) {
                const userDecSep = this.$store.getters['account/user'].MetricDecimalSep;
                const userThouSep = this.$store.getters['account/user'].MetricThousandSep;
                return Utils.number.toUserFormat(
                    parseFloat(value),
                    userDecSep,
                    userThouSep
                );
            }
        },
        async onStateChanged(state) {
            this.settings.SelectedDocumentState = state;
            await this.showData(this.settings, state, false);
        },
        async showData(settings, state, refreshData) {
            this.loading = true;
            this.show();
            this.changes = false;
            this.modifiedDocuments = [];
            this.currentState = state;
            this.settings = settings;

            const documentTrackerData = await WidgetService.getAdditionalData({
                WidgetID: WidgetType.DocumentTracker,
                Settings: settings,
            });

            if (documentTrackerData === null) {
                this.loading = false;
                return;
            }

            this.dataToDisplay = this.mapDocumentsData(documentTrackerData);
            this.listElementsForCodes = documentTrackerData.ListElementsForCodes;

            if (this.dataToDisplay.length > 0) {
                this.dataColumns = this.dataToDisplay[0].fields.map((f) => {
                    return {
                        field: f,
                        name: f.code.replaceAll(' ', '_'),
                        title: f.title,
                        allowEdit:
                            f.type === FieldType.String ||
                            f.type === FieldType.DateTime ||
                            f.type === FieldType.Numeric ||
                            f.type === FieldType.StaticList ||
                            f.type === FieldType.DynamicList ||
                            f.type === FieldType.DynamicTableList,
                        isRequired: f.isRequired,
                        staticLookup: f.type === FieldType.StaticList,
                        dynamicLookup:
                            f.type === FieldType.DynamicList ||
                            f.type === FieldType.DynamicTableList,
                        items: [],
                        visible: this.settings.FieldsToShow.includes(f.code),
                        isNumber: f.type === FieldType.Numeric,
                    };
                });

                this.listElementsForCodes.forEach((c) => {
                    const column = this.dataColumns.find(x => x.field.defFieldId === c.DefFieldID);
                    if (column) {
                        column.items = c.ListElements.map(item => ({
                            displayValue: item.DisplayValue
                        }));
                    }
                });

                this.setNumericColumns();

                const documents = Utils.object.toJS(this.dataSource);

                if (settings.showDocumentOnOpeningModal && documents.length > 0) {
                    this.displayedDocumentId = documents[0].Id;
                    this.loadDocument(documents[0].Id);
                }
            }

            this.documentList = cloneDeep(this.dataToDisplay);

            if (refreshData && this.$refs[this.gridKey]) {
                this.$refs[this.gridKey].instance.repaint();
            }

            this.loading = false;
        },
        setNumericColumns() {
            const userDecSep = this.$store.getters['account/user'].MetricDecimalSep;
            const userThouSep = this.$store.getters['account/user'].MetricThousandSep;

            this.numericColumns = [];
            for (let i = 0; i < this.dataToDisplay[0].fields.length; i++) {
                if (this.dataToDisplay[0].fields[i].type === 2) {
                    this.numericColumns.push(this.dataColumns[i].name);
                    for (let j = 0; j < this.dataToDisplay.length; j++) {
                        this.dataToDisplay[j].fields[i].value = Utils.number.fromUserFormat(
                            this.dataToDisplay[j].fields[i].value,
                            userDecSep,
                            userThouSep
                        );
                    }
                }
            }
        },
        setCellValue(e, data) {
            const documentId = data.key;
            const columnIndex = data.columnIndex;
            data.setValue(e.value);

            const fieldIndex = columnIndex - 1;
            const document = this.documentList.find((d) => d.objectId === documentId);
            const field = document.fields[fieldIndex];
            field.value = e.value;

            if (field.fieldsThatDependOn.length > 0) {
                this.loadRelatedLookups(field.fieldsThatDependOn, document);
            }
            data.component.updateDimensions();
        },
        loadRelatedLookups(fieldsToRefresh, document) {
            fieldsToRefresh.forEach((fieldCode) => {
                let fieldToRefreshIndex = -1;
                for (let i = 0; i < document.fields.length; i++) {
                    if (document.fields[i].code === fieldCode) {
                        fieldToRefreshIndex = i;
                        break;
                    }
                }
                const lookupInstance =
                    this.$refs[`lookup-${document.objectId}-${fieldToRefreshIndex}`];

                if (lookupInstance) {
                    lookupInstance.dataSource.store.clearRawDataCache();
                }
            });
            this.$refs[this.gridKey].instance.repaint();
        },
        getLookupDataSource(documentId, columnIndex) {
            const self = this;
            return {
                store: new CustomStore({
                    key: 'text',
                    loadMode: 'raw',
                    load: () => {
                        const document = self.documentList.find(
                            (d) => d.objectId === documentId
                        );

                        const field = document.fields[columnIndex - 1];

                        const parameters = [];

                        if (field.fieldsOnWhichDepends.length > 0) {
                            field.fieldsOnWhichDepends.forEach((paramCode) => {
                                const value = document.fields.filter(
                                    (d) => d.code === paramCode
                                )[0].value;

                                let paramValue = value;

                                if (paramCode === self.lastChangedCode) {
                                    paramValue = self.lastChangedValue;
                                }

                                let multiValue = false;
                                if (Array.isArray(paramValue)) {
                                    paramValue = paramValue.filter((x) => x).join(',');
                                    multiValue = true;
                                }
                                parameters.push({
                                    Name: paramCode,
                                    Value: paramValue || '',
                                    Multivalue: multiValue,
                                });
                            });
                        }

                        var filterQuery = {
                            Parameters: parameters,
                        };

                        return ContentTypeService.getListElementsByDefField(
                            field.defFieldId,
                            filterQuery
                        ).then((result) => {
                            return result;
                        });
                    },
                }),
            };
        },
        onRowPrepared(e) {
            if (e.rowType === 'data') {
                if (e.data.State === 'Expired') {
                    e.rowElement.style.backgroundColor =
                        this.settings.expiredColor || '#ff5252';
                    e.rowElement.style.color = this.settings.expiredTextColor || 'white';
                }
                if (e.data.State === 'Warning') {
                    e.rowElement.style.backgroundColor =
                        this.settings.warningColor || '#fb8c00';
                    e.rowElement.style.color = this.settings.warningTextColor || 'white';
                }

                if (e.data.Id === this.displayedDocumentId) {
                    e.rowElement.style.border = 'solid 2px #000000';
                }
            }
        },
        onEditingStart() {
            this.documentList = cloneDeep(this.dataToDisplay);
        },
        onRowUpdated(row) {
            const modifiedDocumentIndex = this.modifiedDocuments.findIndex(
                (d) => d.ObjectID === row.data.Id
            );
            if (modifiedDocumentIndex === -1) {
                DocumentService.get(row.data.Id).then((data) => {
                    this.modifiedDocuments.push(data);
                    this.applyChanges(this.modifiedDocuments.length - 1, row);
                });
            } else {
                this.applyChanges(modifiedDocumentIndex, row);
            }
        },
        applyChanges(modifiedDocumentIndex, row) {
            this.changes = true;

            const keysArray = Object.keys(row.data);
            for (const field in this.modifiedDocuments[modifiedDocumentIndex].Fields) {
                const index = keysArray.indexOf(
                    this.modifiedDocuments[modifiedDocumentIndex].Fields[field].Code
                );
                if (index !== -1) {
                    this.modifiedDocuments[modifiedDocumentIndex].Fields[field].Value =
                        this.modifiedDocuments[modifiedDocumentIndex].Fields[field].Type === FieldType.DateTime
                            ? Utils.date
                                .changeFormat(
                                    this.toISODate(row.data[keysArray[index]]),
                                    'yyyy-MM-dd',
                                    this.$store.getters['account/user'].MetricDateFormat
                                )
                                .toString()
                            : row.data[keysArray[index]];
                }
            }
        },
        saveChanges() {
            this.isLoading = true;
            this.modalInstance.progress(true);

            const docList = Utils.object.toJS(this.modifiedDocuments);

            DocumentService.updateMultiple(docList)
                .then(() => {
                    this.$emit('on-documents-updated');

                    this.$nextTick(() => {
                        this.hide();
                        this.modifiedDocuments = [];
                        this.changes = false;
                        Utils.toast.success(this.$t('DocumentsUpdatedSuccessfully'));
                    });
                })
                .catch(() => {
                    Utils.toast.error(this.$t('Site.Widgets.UpdatedError'));
                })
                .finally(() => {
                    this.isLoading = false;
                    this.modalInstance.progress(false);
                });
        },

        toISODate(userDate) {
            const userFormat = this.$store.getters['account/user'].MetricDateFormat;
            let datePartIndex = userFormat.toUpperCase().indexOf('YYYY');
            let yearSubString = 4;
            if (datePartIndex <= 0) {
                datePartIndex = userFormat.toUpperCase().indexOf('YY');
                yearSubString = 2;
            }
            let year = userDate.substr(datePartIndex, yearSubString);
            if (year.length === 2) {
                year = '20' + year;
            }
            datePartIndex = userFormat.toUpperCase().indexOf('MM');
            const month = userDate.substr(datePartIndex, 2);

            datePartIndex = userFormat.toUpperCase().indexOf('DD');
            const day = userDate.substr(datePartIndex, 2);

            return `${year}-${month}-${day}`;
        },
        onToolbarPreparing(e) {
            e.toolbarOptions.items.unshift({
                location: 'after',
                template: 'exportButtonTemplate',
            });
            e.toolbarOptions.items.unshift({
                location: 'after',
                template: 'checkData',
            });
        },
        exportGrid() {
            this.$refs[this.gridKey].instance.exportToExcel(false);
        },
        onExporting(e) {
            const self = this;
            //e.fileName = `Doc.ECM - ${this.widgetTitle}`;
            const workbook = new Workbook();
            const worksheet = workbook.addWorksheet(this.widgetTitle);

            exportDataGrid({
                component: e.component,
                worksheet: worksheet,
                topLeftCell: { row: 1, column: 1 },
                customizeCell: function (options) {
                    const { gridCell, excelCell } = options;
                    if (gridCell.rowType === 'data') {
                        if (gridCell.data.State === 'Expired') {
                            excelCell.fill = {
                                type: 'pattern',
                                pattern: 'solid',
                                fgColor: { argb: 'ff5252' },
                            };
                        }
                        if (gridCell.data.State === 'Warning') {
                            excelCell.fill = {
                                type: 'pattern',
                                pattern: 'solid',
                                fgColor: { argb: 'fb8c00' },
                            };
                        }
                    }
                },
            }).then(function () {
                workbook.xlsx.writeBuffer().then(function (buffer) {
                    saveAs(
                        new Blob([buffer], { type: 'application/octet-stream' }),
                        `Doc.ECM - ${self.widgetTitle}.xlsx`
                    );
                });
            });
            e.cancel = true;
        },
        onSelectionChanged(event) {
            this.selectedDocuments = event.selectedRowKeys;
        },
        validate() {
            const self = this;
            if (this.selectedDocuments.length === 0) {
                Utils.toast.warning(this.$t('DocumentSelectionError'));
                return;
            }
            this.$confirm(this.$t('DocumentsValidationConfirm'), {
                title: this.$t('Validate'),
                icon: 'mdi-warning',
                color: 'green',
                buttonTrueText: this.$t('Validate'),
                buttonFalseText: this.$t('Cancel'),
            }).then((res) => {
                if (res === 1) {
                    const ObjectIds = [];
                    for (let i = 0; i < self.selectedDocuments.length; i++) {
                        ObjectIds.push({ ObjectID: self.selectedDocuments[i] });
                    }
                    self.validationModalInstance.new(ObjectIds, 'V');
                }
            });
        },
        refuse() {
            const self = this;
            if (this.selectedDocuments.length === 0) {
                Utils.toast.warning(this.$t('DocumentSelectionError'));
                return;
            }
            this.$confirm(this.$t('DocumentsRefuseConfirm'), {
                title: this.$t('Refuse'),
                icon: 'mdi-warning',
                color: 'red',
                buttonTrueText: this.$t('Refuse'),
                buttonFalseText: this.$t('Cancel'),
            }).then((res) => {
                if (res === 1) {
                    const ObjectIds = [];
                    for (let i = 0; i <= self.selectedDocuments.length; i++) {
                        ObjectIds.push({ ObjectID: self.selectedDocuments[i] });
                    }
                    self.validationModalInstance.new(ObjectIds, 'R');
                }
            });
        },
        onValidated() {
            this.$emit('on-documents-validated');
        },
        loadDocument(documentId) {
            this.displayedDocumentId = documentId;
            this.$nextTick(async () => {
                const self = this;
                const canDisplay = await DocumentService.canDisplay(documentId);
                self.file = encodeURIComponent('/static/defaultDocument.pdf');
                if (canDisplay) {
                    const fileUrl = await DocumentService.display(documentId);
                    self.file = encodeURIComponent(fileUrl);
                    self.showDocument = true;
                }

                this.$refs[this.gridKey].instance.repaint();
            });
        },
        loadPlugin(plugin, documentIds) {
            const self = this;
            SearchService.getDocuments(documentIds).then((documents) => {
                self.$refs[this.pluginLoaderModalKey].load(plugin, documents);
            });
        },
        loadToolbarPluginList() {
            if (this.selectedDocuments.length !== 0)
                PluginService.getAuthorizedList(
                    'ResultViewToolbar',
                    this.selectedDocuments
                ).then((result) => {
                    this.toolbarPluginList = result.map(function (dto) {
                        return new Plugin(dto);
                    });
                });
        },
        loadPluginList(documentId) {
            PluginService.getAuthorizedList('ResultViewDocument', [documentId]).then(
                (result) => {
                    this.pluginList = result.map(function (dto) {
                        return new Plugin(dto);
                    });
                }
            );
        },
        show() {
            this.open = true;
        },
        hide() {
            this.open = false;
        },
    },
};
</script>
<style>
.custom-radio .v-input--selection-controls__ripple {
    width: 24px;
    height: 24px;
    top: -7px ! important;
    left: -7px ! important;
}
</style>
